import React from 'react';
import { useSelector } from 'react-redux';
import { Theme, Typography, Grid } from '@mui/material';
import { WithStyles } from '@mui/styles';
import createStyles from '@mui/styles/createStyles';
import withStyles from '@mui/styles/withStyles';
import { Shopper, ActiveState, UserDetailName } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import {
	getConvertedUserDetails,
	getDetailDisplayLabel,
	getLocalInputFromMeasurement,
	getMetricInputFromUserProperty,
	getSortedShopperProperties,
} from 'common/utils/userDetails';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getSegmentTranslation, getUserDetailTranslation } from 'common/utils/translations';

interface OwnProps {
	shopper: Shopper;
	activeState: ActiveState;
}

type Props = OwnProps & WithStyles<typeof styles>;

export const ShopperDetails = ({ ...props }: Props) => {
	const { shopper, classes } = props;
	const { segment } = shopper;
	const { t } = useTranslation();

	const shopUnitSystem = useSelector(ShopSelectors.getShopUnitSystem);
	const sortedShopperDetails = getSortedShopperProperties(shopper);

	return (
		<Grid container spacing={2}>
			{segment ? (
				<Grid item>
					<Typography className={classes.userProperty} variant={'h6'}>
						{getSegmentTranslation(segment, t)}
					</Typography>
					<Typography className={classes.lightFont} variant="caption">
						{t('shopperCard.ageGroup', 'Age group')}
					</Typography>
				</Grid>
			) : null}
			{sortedShopperDetails.map(({ detailName, detailValue }, index) => {
				const metricDetail = getMetricInputFromUserProperty(detailValue);
				const localDetail = getConvertedUserDetails(
					detailName as UserDetailName,
					metricDetail,
					shopUnitSystem,
				);
				const localInput = getLocalInputFromMeasurement(localDetail);
				const caption = getUserDetailTranslation(detailName as UserDetailName, t);
				return (
					<Grid item key={detailName}>
						<Typography className={classes.userProperty} variant={'h6'} color="textPrimary">
							{getDetailDisplayLabel(detailName as UserDetailName, localInput, t)}
						</Typography>
						<Typography className={classes.lightFont} variant="caption">
							{caption}
						</Typography>
					</Grid>
				);
			})}
		</Grid>
	);
};

const styles = (theme: Theme) =>
	createStyles({
		sectionHeader: {
			marginBottom: '10px',
		},
		shopperDetailsContainer: {
			display: 'flex',
		},
		lightFont: {
			lineHeight: 1.2,
		},
		userProperty: {
			lineHeight: 1.2,
			fontSize: '1.7rem',
		},
		shopperDetailSmall: {
			marginRight: '16px',
		},
		shopperDetailLarge: {
			marginRight: '32px',
		},
		shopperDetailContainerActive: {
			display: 'flex',
			flexWrap: 'wrap',
		},
	});

export default withStyles(styles)(ShopperDetails);
